import { useState, useEffect } from 'react';
import { MapPin, ChevronRight, Plus, X } from 'lucide-react';
import type { JobTargetType, JobTargetInput } from '../../types/jobs';
import orchardService from '../../services/orchard';
import type { NumericValue } from '@/utils/numeric';

interface JobTargetSelectorProps {
  targets: JobTargetInput[];
  onChange: (targets: JobTargetInput[]) => void;
  onFarmIdsChange?: (farmIds: number[]) => void;
  error?: string;
}

interface FarmOption {
  id: number;
  name: string;
  total_area_hectares?: NumericValue;
}

interface BlockOption {
  id: number;
  farm_id: number;
  name: string;
  area_hectares?: NumericValue;
}

interface CultivarAreaOption {
  id: number;
  farm_id: number;
  block_id: number;
  name: string;
  area_hectares?: NumericValue;
  block_name?: string;
}

function JobTargetSelector({ targets, onChange, onFarmIdsChange, error }: JobTargetSelectorProps) {
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [blocks, setBlocks] = useState<BlockOption[]>([]);
  const [cultivarAreas, setCultivarAreas] = useState<CultivarAreaOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Picker state: null = picker hidden, 'farm' = picking farm, number = picked farm id
  const [pickerFarmId, setPickerFarmId] = useState<number | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [fRes, bRes, aRes] = await Promise.all([
          orchardService.getAllFarms(),
          orchardService.getAllBlocks(),
          orchardService.getAllCultivarAreas(),
        ]);
        if (fRes.success) setFarms((fRes.data as any)?.farms || []);
        if (bRes.success) setBlocks((bRes.data as any)?.blocks || []);
        if (aRes.success) setCultivarAreas((aRes.data as any)?.areas || []);
      } catch (e) {
        console.error('Target load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Emit the set of farm IDs implied by the selected targets so parents can
  // restrict farm-scoped pickers (equipment, etc.).
  useEffect(() => {
    if (!onFarmIdsChange) return;
    const ids = new Set<number>();
    for (const t of targets) {
      if (t.type === 'farm') ids.add(t.id);
      else if (t.type === 'block') {
        const b = blocks.find(x => x.id === t.id);
        if (b) ids.add(b.farm_id);
      } else if (t.type === 'cultivar_area') {
        const a = cultivarAreas.find(x => x.id === t.id);
        if (a) ids.add(a.farm_id);
      }
    }
    onFarmIdsChange(Array.from(ids).sort((a, b) => a - b));
  }, [targets, blocks, cultivarAreas, onFarmIdsChange]);

  const isSelected = (type: JobTargetType, id: number) =>
    targets.some(t => t.type === type && t.id === id);

  const addTarget = (type: JobTargetType, id: number) => {
    if (isSelected(type, id)) return;
    onChange([...targets, { type, id }]);
    setShowPicker(false);
    setPickerFarmId(null);
  };

  const removeTarget = (type: JobTargetType, id: number) => {
    onChange(targets.filter(t => !(t.type === type && t.id === id)));
  };

  const getTargetLabel = (t: JobTargetInput) => {
    if (t.type === 'farm') {
      const f = farms.find(f => f.id === t.id);
      return { name: f?.name ?? `Farm #${t.id}`, area: f?.total_area_hectares, typeLabel: 'Farm' };
    }
    if (t.type === 'block') {
      const b = blocks.find(b => b.id === t.id);
      return { name: b?.name ?? `Block #${t.id}`, area: b?.area_hectares, typeLabel: 'Block' };
    }
    const a = cultivarAreas.find(a => a.id === t.id);
    return { name: a?.name ?? `Area #${t.id}`, area: a?.area_hectares, typeLabel: 'Cultivar Area' };
  };

  const farmBlocks = blocks.filter(b => b.farm_id === pickerFarmId);
  const farmAreas = cultivarAreas.filter(a => a.farm_id === pickerFarmId);
  const pickerFarm = farms.find(f => f.id === pickerFarmId);

  const totalArea = targets.reduce((sum, t) => {
    const label = getTargetLabel(t);
    return sum + (label.area ? Number(label.area) : 0);
  }, 0);

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">
        Target Areas <span className="text-neutral-400 font-normal">(optional — select one or more)</span>
      </label>

      {/* Selected targets list */}
      {targets.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {targets.map((t, _i) => {
            const label = getTargetLabel(t);
            return (
              <div key={`${t.type}-${t.id}`} className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                <MapPin className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="text-xs text-indigo-400 shrink-0">{label.typeLabel}:</span>
                <span className="text-sm font-medium text-indigo-800 flex-1 min-w-0 truncate">{label.name}</span>
                {label.area != null && (
                  <span className="text-xs text-indigo-500 shrink-0">{Number(label.area).toFixed(1)} ha</span>
                )}
                <button
                  type="button"
                  onClick={() => removeTarget(t.type, t.id)}
                  className="ml-1 text-indigo-400 hover:text-red-500 shrink-0"
                  aria-label="Remove target"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          {targets.length > 1 && totalArea > 0 && (
            <div className="text-xs text-neutral-500 pl-1">
              Total: {totalArea.toFixed(1)} ha across {targets.length} areas
            </div>
          )}
        </div>
      )}

      {/* Add target button */}
      {!showPicker && (
        <button
          type="button"
          onClick={() => { setShowPicker(true); setPickerFarmId(null); }}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 border border-dashed border-indigo-300 rounded-lg px-3 py-2 w-full justify-center hover:bg-indigo-50 transition-colors"
        >
          <Plus className="h-4 w-4" />
          {loading ? 'Loading...' : targets.length === 0 ? 'Add target area' : 'Add another area'}
        </button>
      )}

      {/* Picker panel */}
      {showPicker && (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          {/* Step 1: Pick a farm */}
          {pickerFarmId === null && (
            <div>
              <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-100 flex items-center justify-between">
                <span className="text-xs font-medium text-neutral-500">Select a farm</span>
                <button type="button" onClick={() => { setShowPicker(false); }} className="text-xs text-neutral-400 hover:text-neutral-600">Cancel</button>
              </div>
              {farms.length === 0 ? (
                <p className="text-sm text-neutral-400 p-3">No farms found.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  {farms.map(farm => (
                    <button
                      key={farm.id}
                      type="button"
                      onClick={() => setPickerFarmId(farm.id)}
                      className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 flex items-center justify-between border-b border-neutral-50 last:border-0"
                    >
                      <span className="text-sm font-medium text-neutral-800">{farm.name}</span>
                      <div className="flex items-center gap-2">
                        {farm.total_area_hectares && (
                          <span className="text-xs text-neutral-400">{Number(farm.total_area_hectares).toFixed(1)} ha</span>
                        )}
                        <ChevronRight className="h-4 w-4 text-neutral-300" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Pick blocks/areas within the farm */}
          {pickerFarmId !== null && pickerFarm && (
            <div>
              <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setPickerFarmId(null)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                    &larr; Farms
                  </button>
                  <span className="text-xs text-neutral-400">/</span>
                  <span className="text-xs font-medium text-neutral-700">{pickerFarm.name}</span>
                </div>
                <button type="button" onClick={() => { setShowPicker(false); setPickerFarmId(null); }} className="text-xs text-neutral-400 hover:text-neutral-600">Cancel</button>
              </div>

              <div className="max-h-60 overflow-y-auto">
                {/* Entire farm option */}
                <button
                  type="button"
                  onClick={() => addTarget('farm', pickerFarm.id)}
                  disabled={isSelected('farm', pickerFarm.id)}
                  className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-default flex items-center justify-between border-b border-neutral-100"
                >
                  <div>
                    <span className="text-sm font-medium text-indigo-700">Entire farm</span>
                    <span className="text-xs text-neutral-400 ml-2">{pickerFarm.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {pickerFarm.total_area_hectares && (
                      <span className="text-xs text-neutral-400">{Number(pickerFarm.total_area_hectares).toFixed(1)} ha</span>
                    )}
                    {isSelected('farm', pickerFarm.id) && <span className="text-xs text-indigo-500">Added</span>}
                  </div>
                </button>

                {/* Blocks */}
                {farmBlocks.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 bg-neutral-50 text-xs font-medium text-neutral-500 border-b border-neutral-100">
                      Blocks
                    </div>
                    {farmBlocks.map(block => (
                      <button
                        key={`block-${block.id}`}
                        type="button"
                        onClick={() => addTarget('block', block.id)}
                        disabled={isSelected('block', block.id)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-default flex items-center justify-between border-b border-neutral-50 last:border-0"
                      >
                        <span className="text-sm text-neutral-800">{block.name}</span>
                        <div className="flex items-center gap-2">
                          {block.area_hectares && (
                            <span className="text-xs text-neutral-400">{Number(block.area_hectares).toFixed(1)} ha</span>
                          )}
                          {isSelected('block', block.id) && <span className="text-xs text-indigo-500">Added</span>}
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {/* Cultivar Areas */}
                {farmAreas.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 bg-neutral-50 text-xs font-medium text-neutral-500 border-b border-neutral-100">
                      Cultivar Areas
                    </div>
                    {farmAreas.map(area => (
                      <button
                        key={`area-${area.id}`}
                        type="button"
                        onClick={() => addTarget('cultivar_area', area.id)}
                        disabled={isSelected('cultivar_area', area.id)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-default flex items-center justify-between border-b border-neutral-50 last:border-0"
                      >
                        <div>
                          <span className="text-sm text-neutral-800">{area.name}</span>
                          {area.block_name && <span className="text-xs text-neutral-400 ml-2">({area.block_name})</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {area.area_hectares && (
                            <span className="text-xs text-neutral-400">{Number(area.area_hectares).toFixed(1)} ha</span>
                          )}
                          {isSelected('cultivar_area', area.id) && <span className="text-xs text-indigo-500">Added</span>}
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {farmBlocks.length === 0 && farmAreas.length === 0 && (
                  <p className="text-sm text-neutral-400 p-3">No blocks or cultivar areas in this farm yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export default JobTargetSelector;
